import { formatDecimal } from '@/components/table/table-format-helpers';
import { themeColors } from '@/theme/colors';
import { normalizeNumericString } from '@/utils/number';
import {
  Button,
  Checkbox,
  Dropdown,
  Label,
  Modal,
  TextField,
} from '@lego/connect-components-react';
import { Flex, Text } from '@lego/klik-ui';
import { type RefObject, useEffect, useRef, useState } from 'react';
import {
  type BaseRowData,
  type EditableField,
  LockableField,
} from './table-types';

type MassUpdateModalProps = {
  isOpen: boolean;
  lockedColumn: LockableField | undefined;
  selectedRows: BaseRowData[];
  allowEditingVat?: boolean;
  triggerRef: RefObject<HTMLButtonElement | null>;
  onClose: () => void;
  onSubmit: (
    selectedField: EditableField | LockableField,
    value: number,
    isChangeProportional: boolean,
  ) => void;
};

const MassUpdateModal = ({
  isOpen,
  lockedColumn,
  selectedRows,
  allowEditingVat = false,
  triggerRef,
  onClose,
  onSubmit,
}: MassUpdateModalProps) => {
  const [value, setValue] = useState<string>('');
  const sameValue: Record<EditableField, boolean> = selectedRows.reduce(
    (acc, row) => {
      const fields: EditableField[] = [
        LockableField.rrp,
        LockableField.rm,
        LockableField.lp,
        'vat',
      ];
      for (const field of fields) {
        if (acc[field] === false) continue; //already different, no need to check further
        const currentValue = (row as Record<string, unknown>)[field];
        const firstValue = (selectedRows[0] as Record<string, unknown>)[field];
        if (currentValue !== firstValue) {
          acc[field] = false;
        }
      }
      return acc;
    },
    {
      rrp: true,
      rm: true,
      lp: true,
      vat: true,
    } as Record<EditableField, boolean>,
  );

  const handleClose = () => {
    setSelectedField(
      lockedColumn === 'rrp' ? LockableField.rm : LockableField.rrp,
    );
    setIsAdjustProportionallyOn(false);
    setValue('');
    onClose();
  };
  const fieldsWithPercentage = ['rm', 'vat'];
  const [selectedField, setSelectedField] = useState<EditableField>(
    lockedColumn === 'rrp' ? LockableField.rm : LockableField.rrp,
  );
  const [isAdjustProportionallyOn, setIsAdjustProportionallyOn] =
    useState(false);
  const selectedFieldWithPercentage = //hide the option to adjust proportonally if this is true
    selectedField !== null && fieldsWithPercentage.includes(selectedField);

  // biome-ignore lint/correctness/useExhaustiveDependencies: run when modal opens or locked column changes
  useEffect(() => {
    if (!isOpen) return;
    const defaultField =
      lockedColumn === 'rrp' ? LockableField.rm : LockableField.rrp;
    setSelectedField(defaultField);
    if (sameValue[defaultField] && selectedRows[0]) {
      const newValue = fieldsWithPercentage.includes(defaultField)
        ? Number(selectedRows[0][defaultField]) * 100
        : selectedRows[0][defaultField];
      setValue(formatDecimal(newValue ?? 0, 6));
    } else {
      setValue('Multiple values');
    }
  }, [isOpen, lockedColumn]);

  const modalRef = useRef<HTMLDivElement>(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size="small"
      triggerRef={triggerRef}
      ref={modalRef}
    >
      <Modal.Title>Edit selected</Modal.Title>
      <Modal.Content>
        <Text className={'body-sm-regular'}>
          {selectedRows.length} products selected
        </Text>
        <Flex gap="10px" direction={'column'}>
          <Label forId="field">Select a value to edit</Label>
          <Dropdown
            portalTarget={modalRef.current}
            id="field"
            placeholder="Value"
            isOpen={isDropdownOpen}
            onOpenChange={setIsDropdownOpen}
            onChange={(e, value) => {
              const newField = value?.[0]?.value as EditableField;
              if (!newField) return;
              if (fieldsWithPercentage.includes(newField)) {
                setIsAdjustProportionallyOn(false);
              }
              setSelectedField(newField);
              if (sameValue[newField] && selectedRows[0]) {
                const newValue = fieldsWithPercentage.includes(newField)
                  ? Number(selectedRows[0][newField]) * 100
                  : selectedRows[0][newField];
                setValue(formatDecimal(newValue ?? 0, 6));
              } else {
                setValue('Multiple values');
              }
            }}
            fullWidth
            value={[selectedField]}
          >
            <Dropdown.Option value="rrp" disabled={lockedColumn === 'rrp'}>
              RRP
            </Dropdown.Option>
            <Dropdown.Option value="rm" disabled={lockedColumn === 'rm'}>
              RM%
            </Dropdown.Option>
            <Dropdown.Option value="lp" disabled={lockedColumn === 'lp'}>
              LP
            </Dropdown.Option>
            {allowEditingVat && (
              <Dropdown.Option value="vat">VAT%</Dropdown.Option>
            )}
          </Dropdown>
          <Flex gap={'10px'} alignItems={'center'}>
            {isAdjustProportionallyOn && (
              <Flex gap={4} alignItems={'center'}>
                <Button
                  aria-label="minus"
                  isIconOnly
                  iconName="control-minus-solid"
                  onClick={() => {
                    const num = Number(normalizeNumericString(value));
                    setValue(formatDecimal(num - 1, 6));
                  }}
                  appearance="outline"
                  size="small"
                />
                <Button
                  aria-label="plus"
                  isIconOnly
                  iconName="control-plus-solid"
                  onClick={() => {
                    const num = Number(normalizeNumericString(value));
                    setValue(formatDecimal(num + 1, 6));
                  }}
                  size="small"
                />
              </Flex>
            )}
            <TextField
              id="mass-update-value"
              placeholder="Enter value"
              value={value}
              onFocus={
                value === 'Multiple values' ? () => setValue('') : undefined
              }
              onChange={(e) => setValue(e.target.value)}
              style={{ color: themeColors.content.primary }}
              renderEndEnhancer={
                isAdjustProportionallyOn || selectedFieldWithPercentage
                  ? () => (
                      <TextField.Enhancer
                        iconName="percent"
                        aria-label="percent"
                      />
                    )
                  : undefined
              }
            />
          </Flex>
        </Flex>
        {!selectedFieldWithPercentage ? (
          <Checkbox
            label={'Adjust proportionally'}
            checked={isAdjustProportionallyOn}
            onChange={() => {
              setIsAdjustProportionallyOn(!isAdjustProportionallyOn);
              if (!isAdjustProportionallyOn) {
                setValue('0');
              } else if (
                sameValue[selectedField as EditableField] &&
                selectedRows[0]
              ) {
                const currentValue =
                  selectedRows[0][selectedField as EditableField];
                const newValue = Number(currentValue);
                setValue(formatDecimal(newValue ?? 0, 6));
              } else setValue('Multiple values');
            }}
          />
        ) : (
          <Text className="body-md-regular" color={themeColors.content.subtle}>
            This field is already a percentage. Enter the new value directly.
          </Text>
        )}
      </Modal.Content>
      <Modal.Footer>
        <Button appearance="ghost" variant="neutral" onClick={handleClose}>
          Cancel
        </Button>
        <Button
          appearance="solid"
          onClick={() => {
            if (selectedField !== null) {
              const numericValue = Number(normalizeNumericString(value));
              if (isAdjustProportionallyOn) {
                onSubmit(selectedField, numericValue, true);
              } else {
                const newValue = selectedFieldWithPercentage
                  ? numericValue / 100
                  : numericValue;
                onSubmit(selectedField, newValue, false);
              }
            }
            handleClose();
          }}
          disabled={value === ''}
        >
          Update Prices
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
export default MassUpdateModal;
